import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { templates } from '@repletoryreach/sdk-api';
import { Card, Stack, Title, Text, Badge, Group, Button } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatDateTime, formatRelative, shortId } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';
import { MetaRefusalNotice, refusalFrom, type Refusal } from '../../../numbers/MetaRefusalNotice';

type Template = NonNullable<Awaited<ReturnType<typeof templates.get>>['data']>;

const MUTED = 'var(--rr-color-text-muted, #94a3b8)';

function statusColor(s: string): string {
  if (s === 'approved') return 'green';
  if (s === 'rejected') return 'red';
  if (s === 'paused') return 'orange';
  if (s === 'submitted') return 'blue';
  return 'gray';
}

interface Step {
  label: string;
  at: string | null;
  state: 'done' | 'pending' | 'failed';
  detail?: React.ReactNode;
}

export function LifecycleTab({ template }: { template: Template }) {
  const t = useT();
  const qc = useQueryClient();
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const submit = useMutation({
    mutationFn: () => templates.submit(template.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['template', template.id] });
      qc.invalidateQueries({ queryKey: ['templates'] });
      setRefusal(null);
      notifySuccess(t('templates.toast.submitted'), t('status.submitted'));
    },
    onError: (err) => {
      // A refusal from WhatsApp carries its own next step (and, for a number
      // still on the WhatsApp Business app, the way out), so it is rendered
      // in the tab rather than flashed in a toast.
      const r = refusalFrom(err);
      if (r) { setRefusal(r); return; }
      notifyError(err instanceof Error ? err.message : t('templates.view.lifecycle.submitFailed'), t('templates.toast.submitFailed'));
    }
  });

  const steps: Step[] = [
    { label: t('templates.view.lifecycle.draftedAt'), at: template.created_at, state: 'done' },
    {
      label: t('templates.view.lifecycle.submittedAt'),
      at: template.submitted_at,
      state: template.submitted_at ? 'done' : 'pending'
    },
    {
      label: t('templates.view.lifecycle.title'),
      at: template.approved_at,
      /*
       * §4 item 3: this keyed `failed` off `rejection_reason` alone, and the
       * service never clears that on re-submit, so a `submitted` template
       * showed a red rejected step under a blue "submitted" badge. The detail
       * alert two lines below already gated on the status correctly.
       */
      state: template.approved_at
        ? 'done'
        : template.status === 'rejected'
          ? 'failed'
          : 'pending',
      detail:
        template.status === 'rejected' && template.rejection_reason ? (
          <Alert color="red" variant="light">{template.rejection_reason}</Alert>
        ) : undefined
    }
  ];

  return (
    <Stack gap="md">
      {refusal && <MetaRefusalNotice refusal={refusal} onClose={() => setRefusal(null)} />}
      <Card withBorder>
        <Stack gap="sm">
          <Group justify="space-between" align="baseline">
            <Title order={4}>{t('broadcasts.view.tabs.lifecycle')}</Title>
            <Badge size="sm" variant="light" color={statusColor(template.status)}>
              {template.status}
            </Badge>
          </Group>
          <Stack gap={0}>
            {steps.map((step, i) => (
              <Group
                key={i}
                gap="md"
                align="flex-start"
                wrap="nowrap"
                style={{
                  padding: '12px 0',
                  borderBottom:
                    i < steps.length - 1 ? '1px dashed var(--rr-color-border, #1f2937)' : 'none'
                }}
              >
                <div
                  aria-hidden
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    marginTop: 6,
                    background:
                      step.state === 'failed'
                        ? 'var(--rr-color-danger)'
                        : step.state === 'done'
                          ? 'var(--rr-color-success, #10b981)'
                          : 'var(--rr-color-text-muted, #94a3b8)'
                  }}
                />
                <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                  <Group gap="sm" align="baseline">
                    <Text fz="sm">{step.label}</Text>
                    {step.state === 'pending' && (
                      <Badge size="xs" variant="outline" color="gray">{t('status.pending')}</Badge>
                    )}
                    {step.state === 'failed' && (
                      <Badge size="xs" variant="light" color="red">{t('status.rejected')}</Badge>
                    )}
                  </Group>
                  <Text fz="xs" c={MUTED}>
                    {step.at
                      ? `${formatDateTime(step.at)} (${formatRelative(step.at)})`
                      : '–'}
                  </Text>
                  {step.detail}
                </Stack>
              </Group>
            ))}
          </Stack>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="sm">
          <Title order={4}>{t('templates.view.lifecycle.metaSync')}</Title>
          <Row
            label={t('templates.view.lifecycle.metaId')}
            value={
              template.meta_template_id ? (
                <code>{shortId(template.meta_template_id)}</code>
              ) : (
                <span style={{ color: MUTED }}>, </span>
              )
            }
          />
          <Row
            label={t('templates.view.lifecycle.metaName')}
            value={template.meta_template_name ?? <span style={{ color: MUTED }}>, </span>}
          />
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="sm">
          <Title order={4}>{t('common.actions')}</Title>
          {template.status === 'draft' && (
            <Alert color="blue" variant="light">
              {t('templates.view.lifecycle.submitHint')}
            </Alert>
          )}
          <Group gap="xs">
            <Button
              size="xs"
              variant="filled"
              color="blue"
              disabled={template.status !== 'draft' && template.status !== 'rejected'}
              loading={submit.isPending}
              onClick={() =>
                openConfirmModal({
                  title: t('templates.view.lifecycle.modal.submit.title'),
                  children: (
                    <Text fz="sm">
                      {t('templates.view.lifecycle.modal.submit.body')}
                    </Text>
                  ),
                  labels: { confirm: t('action.submit'), cancel: t('action.cancel') },
                  confirmProps: { color: 'blue' },
                  onConfirm: () => submit.mutate()
                })
              }
            >
              Submit to Meta
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8, alignItems: 'baseline' }}>
      <Text fz="xs" c={MUTED}>{label}</Text>
      <span style={{ fontSize: 14 }}>{value}</span>
    </div>
  );
}
